/*
 * ENUMS/INDEX.JS - Application Constants and Enum Definitions
 * 
 * SUMMARY:
 * Centralized location for all application constants, enums, and configuration values.
 * Provides consistent values across the application for proposal types, template types,
 * section management, tax types, and pagination settings.
 * 
 * MAJOR ENUMS:
 * - ProposalType - Defines different types of proposals (Summary vs Detailed)
 * - TemplateType - Document template types for various business processes
 * - DefaultSectionOptions - Default values for section management
 * - SectionMenu - Section and subsection management actions
 * - GSTORVAT - Tax type configuration
 * - PerPageCount - Pagination settings for data tables
 * 
 * USE CASES:
 * - Proposal generation and management
 * - Document template selection
 * - Section and subsection CRUD operations
 * - Tax calculation and display
 * - Data pagination and table management
 * - Consistent value references across components
 */

/**
 * Proposal Type Enumeration
 * Defines the different types of proposals that can be generated in the system.
 * 
 * @type {Object}
 * @property {string} SUMMARY - Summary proposal type identifier
 * @property {string} DETAILED - Detailed proposal type identifier
 * 
 * @example
 * if (proposalType === ProposalType.SUMMARY) {
 *   // Handle summary proposal logic
 * }
 */
export const ProposalType = {
    SUMMARY: 'SUMMARY_PROPOSAL',
    DETAILED: 'DETAILED_PROPOSAL'
}

/**
 * Template Type Enumeration
 * Defines the different document template types used for business processes.
 * 
 * @type {Object}
 * @property {string} PURCHASE_ORDER - Purchase order template identifier
 * @property {string} INVOICE - Invoice template identifier
 * @property {string} PROGRESSCLAIM - Progress claim template identifier
 * 
 * @example
 * const templateType = TemplateType.PURCHASE_ORDER;
 * // Use for generating purchase order documents
 */
export const TemplateType = {
    PURCHASE_ORDER: 'ProjectPO',
    INVOICE: 'INVOICE',
    PROGRESSCLAIM: 'PROGRESSCLAIM',
}

/**
 * Default Section Options
 * Provides default values for section management when no specific section is assigned.
 * 
 * @type {Object}
 * @property {string} LABEL - Display label for project-level items
 * @property {string} VALUE - Empty value representing no section assignment
 * 
 * @example
 * const sectionOptions = [
 *   DefaultSectionOptions,
 *   { label: 'Section A', value: 'section-a' },
 *   { label: 'Section B', value: 'section-b' }
 * ];
 */
export const DefaultSectionOptions = {
    LABEL: 'Project Level (No Section)',
    VALUE: '',
}

/**
 * Section Menu Actions
 * Defines the available actions for section and subsection management.
 * 
 * @type {Object}
 * @property {string} TYPE - Section type identifier
 * @property {string} ADD - Add section action
 * @property {string} ADDSUBSECTION - Add subsection action
 * @property {string} DELETE - Delete section action
 * @property {string} DELETESUBSECTION - Delete subsection action
 * @property {string} EDIT - Edit section action
 * @property {string} EDITSUBSECTION - Edit subsection action
 * 
 * @example
 * const handleSectionAction = (action) => {
 *   switch (action) {
 *     case SectionMenu.ADD:
 *       // Handle add section
 *       break;
 *     case SectionMenu.EDIT:
 *       // Handle edit section
 *       break;
 *   }
 * };
 * 
 * @example
 * const sectionActions = [
 *   SectionMenu.ADD,
 *   SectionMenu.ADDSUBSECTION,
 *   SectionMenu.EDIT,
 *   SectionMenu.DELETE
 * ];
 */
export const SectionMenu = {
    TYPE: 'SECTION',
    ADD: 'ADD SECTION',
    ADDSUBSECTION: 'ADD SUBSECTION',
    DELETE: 'DELETE SECTION',
    DELETESUBSECTION: 'DELETE SUBSECTION',
    EDIT: 'EDIT SECTION',
    EDITSUBSECTION: 'EDIT SUBSECTION',
}

/**
 * Tax Type Configuration
 * Defines the tax type used throughout the application (GST or VAT).
 * 
 * @type {string}
 * @description Currently set to GST (Goods and Services Tax)
 * 
 * @example
 * const taxLabel = GSTORVAT === 'GST' ? 'GST' : 'VAT';
 * 
 * @example
 * const taxRate = getTaxRate(GSTORVAT);
 * // Returns appropriate tax rate based on tax type
 */
export const GSTORVAT = 'GST';

/**
 * Pagination Settings
 * Defines the available page size options for data tables and lists.
 * 
 * @type {Object}
 * @property {number} DEFAULT - Default page size (25 items)
 * @property {number} CUSTOM - Custom page size option (50 items)
 * @property {number} MAX - Maximum page size (100 items)
 * 
 * @example
 * const pageSizeOptions = [
 *   { label: '25 per page', value: PerPageCount.DEFAULT },
 *   { label: '50 per page', value: PerPageCount.CUSTOM },
 *   { label: '100 per page', value: PerPageCount.MAX }
 * ];
 * 
 * @example
 * const handlePageSizeChange = (newSize) => {
 *   if (newSize <= PerPageCount.MAX) {
 *     setPageSize(newSize);
 *   }
 * };
 */
export const PerPageCount = {
    DEFAULT: 25,
    CUSTOM: 50,
    MAX: 100,
}

export const TaskType = {
    TASK: 'TASK',
    STAGE: 'STAGE',
    VARIATION: 'VARIATION',
    OPTION: 'OPTION'
}

export const PROJECT_TYPE = {
    PROJECT: 'PROJECT',
    PROPOSAL: 'PROPOSAL',
    TENDER: 'TENDER',
    REVISION: 'REVISION',
}

export const PROJECT_STATUS = {
    PENDING: 'PENDING',
    CURRENT: 'CURRENT',
    DLP: 'DLP',
    LOST: 'LOST',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
    TENDER_PENDING: 'TENDER_PENDING',
    TENDER_SUBMITTED: 'TENDER_SUBMITTED',
    NOT_STARTED: 'NOT_STARTED',
    IN_PROGRESS: 'IN_PROGRESS',
    ARCHIVED: 'ARCHIVED',
    COSTED: 'COSTED',
    LOI: 'LOI',
}

export const QUOTE_TYPE = {
    QUOTE: 'quote',
    NOQUOTE: 'noquote',
}

